import React, { useEffect, useRef, useState, memo, useLayoutEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { Icon, Spinner, Typography } from '..'
import { util } from '../../../util'
import { LanguageContext } from '../../../context/LanguageContext'

const Select = props => {
    //Localization
    const { strings } = useContext(LanguageContext)

    const {
        variant,
        viewOnly,
        isOptionsDisabled,
        className,
        color,
        isLoading = false,
        onTextChange,
        error,
        label,
        options,
        name,
        onChange,
        placeholder,
        isReadOnly = true,
        size,
        value,
        isFullWidth = false,
        disabled,
        isOptional,
        onClick,
        searchable = false,
        selectedPillList = [],
        removePillHandler,
        endIcon,
        formatGroupLabel,
    } = props

    const [selectOptions, setSelectOptions] = useState(options)
    const [showOptions, setShowOptions] = useState(false)
    const selectWrapperRef = useRef()
    const selectInputRef = useRef(null)
    const optionsRef = useRef(null)

    const focusSelectInputRef = () => selectInputRef.current.focus()

    useEffect(() => {
        document.addEventListener('mousedown', outerClickHandler, true)
        return () => document.removeEventListener('mousedown', outerClickHandler, true)
    }, [showOptions])

    useLayoutEffect(() => {
        if (selectWrapperRef.current && optionsRef.current && isFullWidth) {
            optionsRef.current.style.width = `${selectWrapperRef.current.offsetWidth}px`
        }
    }, [selectWrapperRef, optionsRef, showOptions, isFullWidth])

    useEffect(() => {
        if (searchable && value.length > 0) {
            const searchList = options.filter(opt =>
                opt.value.toString().toLowerCase().includes(value.toString().toLowerCase())
            )
            if (searchList.length) {
                setShowOptions(true)
            }
            setSelectOptions(searchList)
        }
    }, [searchable, options, value])

    const outerClickHandler = e => {
        if (selectWrapperRef.current && !selectWrapperRef.current.contains(e.target)) {
            setShowOptions(false)
        }
    }

    const onSelectClickHandler = e => {
        /**
         * If the select component is disabled or is in view only state then don't show the select dropdowns
         */
        !isOptionsDisabled && !searchable && !disabled && !viewOnly && setShowOptions(!showOptions)
        !!onClick && onClick(e)
    }

    const onOptionClickHandler = (option, groupLabel) => {
        setSelectOptions(options)
        setShowOptions(false)
        !!onChange && value && onChange(option, name, groupLabel)
    }

    const onTextEdit = e => {
        setShowOptions(false)
        !!onTextChange && onTextChange(e)
    }

    const renderSelectPills = () => {
        if (!searchable) {
            return null
        }
        return selectedPillList.map(pill => {
            return (
                <div className={'select__wrapper__controls--pill'} key={pill.key}>
                    <span className={'mr-2'}>{pill.value}</span>
                    <Icon
                        pointer={true}
                        size={'xxs'}
                        onClick={e => {
                            e.stopPropagation()
                            removePillHandler(pill, name)
                        }}
                        className={'selected-list__cancel'}
                        renderIcon={util.CancelIcon}
                    />
                </div>
            )
        })
    }

    const renderSelectOption = (option, groupLabel) => (
        <div
            onClick={() => onOptionClickHandler(option, groupLabel)}
            className={'select-option'}
            key={option.value}
        >
            {option.label}
        </div>
    )

    const mapSelectOptions = (options, groupLabel) => {
        return options.map(option => {
            return renderSelectOption(option, groupLabel)
        })
    }

    const renderOptions = () => {
        return (
            <div className={`select__options ${showOptions && 'show-options'}`} ref={optionsRef}>
                {isLoading && (
                    <div className={'dropdown-loader'}>
                        <Spinner loading={isLoading} isInline={true} />
                    </div>
                )}
                {selectOptions.map(option => {
                    if (!!option.options && !!formatGroupLabel) {
                        return (
                            <div className={'select__options--group-options'}>
                                <div className={'group-options-label'}>
                                    {formatGroupLabel(option)}
                                </div>
                                <div className={'group-options'}>
                                    {mapSelectOptions(option.options, option.label)}
                                </div>
                            </div>
                        )
                    } else {
                        return renderSelectOption(option)
                    }
                })}
            </div>
        )
    }

    const renderIconProps = () => {
        return showOptions
            ? { size: 'md', renderIcon: util.ArrowDropUpIcon }
            : { size: 'md', renderIcon: util.ArrowDropDownIcon }
    }

    const endIconClass = 'select__wrapper__end-icon'

    const renderEndIcon = () => {
        return !endIcon ? (
            <Icon className={endIconClass} pointer={true} {...renderIconProps()} />
        ) : (
            React.cloneElement(endIcon, {
                className: `${endIconClass} ${
                    endIcon.props.className.length ? endIcon.props.className : ''
                }`,
                onClick: !endIcon.props.onClick ? onSelectClickHandler : endIcon.props.onClick,
            })
        )
    }

    const selectClassName = util.joinClasses(
        'select',
        variant && `variant-${variant}`,
        showOptions && 'open-options',
        disabled && 'select-disabled',
        isReadOnly && 'read-only',
        viewOnly && 'view-only',
        size && `size-${size}`,
        color && `color-${color}`,
        isFullWidth && 'full-width',
        className
    )

    const selectInputClass = util.joinClasses(
        'select__wrapper__controls--input-control',
        (selectedPillList.length || (!!value && value.length)) && 'has-value'
    )

    return (
        <div className={selectClassName} ref={selectWrapperRef}>
            <div className={'select__wrapper'} onClick={onSelectClickHandler}>
                <div className={'select__wrapper__controls'}>
                    {renderSelectPills()}
                    <input
                        autoComplete={'off'}
                        ref={selectInputRef}
                        className={selectInputClass}
                        value={value}
                        onChange={e => onTextEdit(e)}
                        readOnly={isReadOnly}
                        placeholder={placeholder}
                    />
                    <label
                        className={'select__wrapper__controls--label'}
                        onClick={focusSelectInputRef}
                    >
                        {label}
                    </label>
                </div>
                {renderEndIcon()}
            </div>
            {renderOptions()}
            <Typography
                show={!!error && error.length}
                text={error}
                color={'danger'}
                size={'sm'}
                className={'mt-2 mb-2'}
            />
            <Typography
                show={!!(isOptional && !viewOnly && !error)}
                text={strings.OPTIONAL}
                color={'light'}
                isItalic
                size={'sm'}
                className={'select__optional ml-3 mt-2 mb-3'}
            />
        </div>
    )
}

const optionProps = PropTypes.shape([
    PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
])

Select.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    options: optionProps,
    selectedPillList: optionProps,
    isReadOnly: PropTypes.bool,
    searchable: PropTypes.bool,
    name: PropTypes.string,
    size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
}

export default memo(Select)
